// Code generated by contracts/generate.ts. DO NOT EDIT.
//
// Edit the JSON under contracts/data/ and run `pnpm contracts:generate`.
// CI fails if this file is hand-edited or left stale.

/**
 * Firestore top-level collection ids, with the leading slash the backend has
 * always used. Prefer COLLECTION for client-SDK calls, which take a bare id.
 */
export enum CollectionPath {
  /** The user document. See user-data.schema.json. */
  Users = '/users',
  /**
   * Apple refresh tokens, written at sign-in and required to revoke the
   * grant when the account is closed.
   */
  AppleTokens = '/appleTokens',
  /**
   * Audit record of an account an admin deleted, written only by
   * adminDeleteUser via the Admin SDK before the deletion runs. Lives
   * outside users/{uid} because the deletion removes that whole tree. No
   * client may write it.
   */
  DeletedUsers = '/deletedUsers',
  ApprovalQueue = '/approval-queue',
  /**
   * Deferred new-entry notifications for forms whose form-rules doc sets
   * notifyOnNew to 'daily' or 'weekly' — one document per approval-queue
   * entry, written by handleApprovalQueueV2 and drained by
   * sendApprovalQueueDailyDigest / sendApprovalQueueWeeklyDigest.
   * Backend-only: no client reads it, so it has no firestore.rules entry and
   * falls under the default deny.
   */
  ApprovalDigestQueue = '/approval-digest-queue',
  Emails = '/emails',
  EmailContacts = '/emailContacts',
  /**
   * Send queue and durable delivery record for a terms-of-service change
   * notice — one document per account holder per notice version, seeded by
   * startTosNoticeBatch when an admin starts a batch from backstage, and
   * drained at a fixed rate by drainTosNotices. `tosVersion` is the id of
   * the tosNoticeBatches document the notice belongs to. Backend-only: no
   * client reads it, so it has no firestore.rules entry and falls under the
   * default deny.
   */
  TosNotices = '/tosNotices',
  /**
   * Marketing email drafts composed in the backstage email widget.
   * `bodyMarkdown` is the editable source of truth and stays here even after
   * sending, which is what makes duplicating a past email produce something
   * editable — a Sender.net campaign cannot be edited through the API once
   * created. Sender.net is only written to at send/schedule time.
   * Backend-only: every read and write goes through a role-checked callable,
   * so it has no firestore.rules entry and falls under the default deny.
   */
  EmailDrafts = '/emailDrafts',
  /**
   * One document per T&C notice batch an admin starts from the backstage
   * email widget: its status, subject, markdown body, fixed effective date,
   * batch size and the audience report from seeding. The document id doubles
   * as the `tosVersion` of every tosNotices document in the batch and is
   * never reused. Backend-only: every read and write goes through an
   * admin-gated callable or drainTosNotices, so it has no firestore.rules
   * entry and falls under the default deny.
   */
  TosNoticeBatches = '/tosNoticeBatches',
  /**
   * Single-active-batch lock for T&C notices: `current` holds
   * `activeBatchId` while a batch is seeding, running or paused, and
   * `lastBatchId` once it is cancelled or finished. It also holds
   * `savedDraft`, the one saved copy (subject, body, effective date, batch
   * size) of the next batch, which an admin can save without sending and
   * which starting a batch clears. Every write merges so the lock and the
   * draft never clobber each other. Written only by the admin-gated T&C
   * callables and by drainTosNotices when a batch finishes. Backend-only: no
   * firestore.rules entry, default deny.
   */
  TosDrainConfig = '/tosDrainConfig',
  /**
   * Debounce state for website rebuilds. `current` records when a
   * `song-publish` repository_dispatch was last requested and when one last
   * actually fired, so a burst of Song Manager saves queues one GitHub
   * Actions run rather than one each. Written by requestSiteRebuild and by
   * uploadNewSong, both via the Admin SDK. Backend-only: no client reads it,
   * so it has no firestore.rules entry and falls under the default deny.
   */
  SiteRebuilds = '/siteRebuilds',
  FormRules = '/form-rules',
  Forms = '/forms',
  PendingUserData = '/pending-user-data',
  /**
   * Short-lived single-use nonces backing the 'open the website already
   * signed in' hand-off from the mobile app. The nonce (not a custom token)
   * travels in the deep link; only redeemLoginNonce can exchange it for a
   * session, and the rules deny every client read and write so possessing
   * one is worthless otherwise.
   */
  LoginNonces = '/login-nonces',
  /**
   * One document per provider ('apple_app_store', 'google_play'), each
   * holding that provider's transaction mappings.
   */
  IapPurchases = '/iap-purchases',
  /** The song catalog. Readable by any signed-in user. */
  Songs = '/songs',
  /**
   * The genre, mood, deaf and pitch tag vocabularies, one document per tag
   * keyed by its Contentful genre entry id. Contentful authors them (the
   * genre content type's `tagType` field); syncSongTagFromContentful upserts
   * or deletes one document per publish/unpublish webhook, and
   * resyncSongTags rebuilds the whole collection on demand. Songs reference
   * these ids in `genreTags`, `moodTags`, `deafTags` and `pitchTags`.
   * Written only by those functions via the Admin SDK; publicly readable, so
   * logged-out website visitors get the live vocabulary too.
   */
  SongTags = '/song-tags',
  /** Play analytics. Team-member plays are excluded. */
  SongPlays = '/songPlays',
  SongDownloads = '/songDownloads',
  LicenseAgreements = '/license-agreements',
  ContentfulTutorials = '/contentfulTutorials',
  ContentfulTutorialsPreview = '/contentfulTutorialsPreview',
  /**
   * Client debug logs, write-only for the owning user and readable only from
   * the console. One document per user, holding a session per app launch.
   */
  DebugLogging = '/debug-logging',
  /**
   * Party mode. One small document is the whole party: what is playing,
   * where in it, and the shared-clock instant that position was true. The
   * document id is the join code, so a joiner can address the party from the
   * code alone without a query. Host-only writes to the timeline,
   * participants-only reads, capped at 8 participants — see firestore.rules.
   */
  Parties = '/parties',
  /** Written by reconcilePlusEntitlements. */
  ReconcileLog = '/reconcileLog',
}

/** The same collections as bare ids, for `collection(db, ...)` on the client. */
export const COLLECTION = Object.freeze({
  Users: 'users',
  AppleTokens: 'appleTokens',
  DeletedUsers: 'deletedUsers',
  ApprovalQueue: 'approval-queue',
  ApprovalDigestQueue: 'approval-digest-queue',
  Emails: 'emails',
  EmailContacts: 'emailContacts',
  TosNotices: 'tosNotices',
  EmailDrafts: 'emailDrafts',
  TosNoticeBatches: 'tosNoticeBatches',
  TosDrainConfig: 'tosDrainConfig',
  SiteRebuilds: 'siteRebuilds',
  FormRules: 'form-rules',
  Forms: 'forms',
  PendingUserData: 'pending-user-data',
  LoginNonces: 'login-nonces',
  IapPurchases: 'iap-purchases',
  Songs: 'songs',
  SongTags: 'song-tags',
  SongPlays: 'songPlays',
  SongDownloads: 'songDownloads',
  LicenseAgreements: 'license-agreements',
  ContentfulTutorials: 'contentfulTutorials',
  ContentfulTutorialsPreview: 'contentfulTutorialsPreview',
  DebugLogging: 'debug-logging',
  Parties: 'parties',
  ReconcileLog: 'reconcileLog',
} as const);

/**
 * Collections nested under a document. These ids are not unique on their own
 * — `events` appears under both a user's analytics groups and a debug
 * session — so reach them through the path builders below rather than by
 * concatenating.
 */
export const SUBCOLLECTION = Object.freeze({
  /**
   * The user's own copy of a song: the shared track-settings blob plus
   * library state. See song-settings.schema.json.
   */
  MySongs: 'mySongs',
  /**
   * Synced from the mobile app. Deletes are soft, via a `deletedAt`
   * tombstone, so a playlist removed on one device does not resurrect from
   * another.
   */
  Playlists: 'playlists',
  /**
   * Party mode's shared clock. Each device overwrites one throwaway document
   * holding nothing but a server timestamp, then reads the resolved value
   * back to measure its own offset from Firestore. Written and read only by
   * the owning user; nothing else consumes it.
   */
  ClockProbes: 'clockProbes',
  /**
   * One document per event group (the mobile app writes them all under
   * `mobile-events`), each holding an `events` subcollection. Deleted
   * wholesale by handleAccountClosure.
   */
  Analytics: 'analytics',
  AnalyticsEvents: 'events',
  /**
   * Audit trail of administrative changes to a user record, written only by
   * adminUpdateUser via the Admin SDK. No client may write it.
   */
  AdminEdits: 'adminEdits',
  /**
   * Four specific forms accept unauthenticated submissions (the deep-dive
   * payment components, the audiologist CPD register and the Plus sign-up);
   * every other form requires a signed-in user whose uid matches the
   * submission. See firestore.rules.
   */
  FormSubmissions: 'submissions',
  /**
   * Durable mapping from a store transaction id / purchase token back to the
   * Firebase uid, so later App Store and Play server notifications can be
   * correlated to a user without auth context.
   */
  IapTransactions: 'transactions',
  /**
   * Append-only audit trail: a snapshot of the document as it was before
   * each save, written by both the Song Manager and uploadNewSong.
   */
  SongVersions: 'versions',
  /**
   * Successive revisions of an agreement. The parent document holds the
   * current one; this keeps the history a song may have been licensed under.
   */
  LicenseVersions: 'versions',
  DebugSessions: 'sessions',
  DebugSessionEvents: 'events',
} as const);

/** `users/{userId}/mySongs` */
export const MY_SONGS_PATH = (userId: string): string =>
  `${COLLECTION.Users}/${userId}/${SUBCOLLECTION.MySongs}`;

/** `users/{userId}/playlists` */
export const PLAYLISTS_PATH = (userId: string): string =>
  `${COLLECTION.Users}/${userId}/${SUBCOLLECTION.Playlists}`;

/** `users/{userId}/clockProbes` */
export const CLOCK_PROBES_PATH = (userId: string): string =>
  `${COLLECTION.Users}/${userId}/${SUBCOLLECTION.ClockProbes}`;

/** `users/{userId}/analytics` */
export const ANALYTICS_PATH = (userId: string): string =>
  `${COLLECTION.Users}/${userId}/${SUBCOLLECTION.Analytics}`;

/** `users/{userId}/analytics/{analyticsGroupId}/events` */
export const ANALYTICS_EVENTS_PATH = (userId: string, analyticsGroupId: string): string =>
  `${COLLECTION.Users}/${userId}/${SUBCOLLECTION.Analytics}/${analyticsGroupId}/${SUBCOLLECTION.AnalyticsEvents}`;

/** `users/{userId}/adminEdits` */
export const ADMIN_EDITS_PATH = (userId: string): string =>
  `${COLLECTION.Users}/${userId}/${SUBCOLLECTION.AdminEdits}`;

/** `forms/{formId}/submissions` */
export const FORM_SUBMISSIONS_PATH = (formId: string): string =>
  `${COLLECTION.Forms}/${formId}/${SUBCOLLECTION.FormSubmissions}`;

/** `iap-purchases/{provider}/transactions` */
export const IAP_TRANSACTIONS_PATH = (provider: string): string =>
  `${COLLECTION.IapPurchases}/${provider}/${SUBCOLLECTION.IapTransactions}`;

/** `songs/{songId}/versions` */
export const SONG_VERSIONS_PATH = (songId: string): string =>
  `${COLLECTION.Songs}/${songId}/${SUBCOLLECTION.SongVersions}`;

/** `license-agreements/{agreementId}/versions` */
export const LICENSE_VERSIONS_PATH = (agreementId: string): string =>
  `${COLLECTION.LicenseAgreements}/${agreementId}/${SUBCOLLECTION.LicenseVersions}`;

/** `debug-logging/{userId}/sessions` */
export const DEBUG_SESSIONS_PATH = (userId: string): string =>
  `${COLLECTION.DebugLogging}/${userId}/${SUBCOLLECTION.DebugSessions}`;

/** `debug-logging/{userId}/sessions/{sessionId}/events` */
export const DEBUG_SESSION_EVENTS_PATH = (userId: string, sessionId: string): string =>
  `${COLLECTION.DebugLogging}/${userId}/${SUBCOLLECTION.DebugSessions}/${sessionId}/${SUBCOLLECTION.DebugSessionEvents}`;
